import React, { useState } from "react";
import classNames from "classnames";
import {
  XMarkIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  ShareIcon,
  HomeIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import {
  UrbanItem,
  UrbanService,
  PointType,
  POINT_TYPES,
  POINT_STYLE,
  STATUS_STYLE,
  PAY_STYLE,
  URBAN_CITIES,
  URBAN_POINTS,
  CITY_CODE,
  money,
  kg,
  toFloat,
  formatFrDateTime,
} from "./urbain-shared";

// -------------------------------------------------------------
// Pricing
// -------------------------------------------------------------
const BASE_FEE: Record<string, number> = { Douala: 1500, Yaoundé: 1500 };
const DEFAULT_BASE_FEE = 2000;
const HOME_PICKUP_FEE = 1000;
const HOME_DELIVERY_FEE = 1500;
const EXPRESS_FEE = 1500;
const EXTRA_KG_FEE = 200; // per kg above 5 kg
const COD_RATE = 0.02; // min 500 FCFA
const INSURANCE_RATE = 0.01;

type Side = { type: PointType; pointId: string; address: string; quartier: string };

const pointsOf = (city: string, type: PointType) => URBAN_POINTS.filter((p) => p.city === city && p.type === type);

const sidePlace = (s: Side) => (s.type === "Domicile" ? s.address : URBAN_POINTS.find((p) => p.id === s.pointId)?.name ?? "");
const sideQuartier = (s: Side) => (s.type === "Domicile" ? s.quartier : URBAN_POINTS.find((p) => p.id === s.pointId)?.quartier ?? "");
const sideFee = (s: Side) => (s.type === "Domicile" ? 0 : URBAN_POINTS.find((p) => p.id === s.pointId)?.fee ?? 0);

// -------------------------------------------------------------
// WIZARD: NOUVELLE LIVRAISON URBAINE
// -------------------------------------------------------------
interface UrbainWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (item: UrbanItem) => void;
  onFlash: (msg: string) => void;
}

export function UrbainWizardModal({ isOpen, onClose, onSuccess, onFlash }: UrbainWizardModalProps) {
  const [step, setStep] = useState(1);

  // Step 1 — service, city and main point (drop point, or pickup point for a collection-only order)
  const [service, setService] = useState<UrbanService>("Livraison");
  const [city, setCity] = useState("Douala");
  const [filterType, setFilterType] = useState<"Tous" | PointType>("Tous");
  const [searchPoint, setSearchPoint] = useState("");
  const [mainSel, setMainSel] = useState<string | null>(null); // point id or "domicile"

  // Step 2 — the other side of the trip + parties + parcel
  const [otherType, setOtherType] = useState<PointType>("Agence");
  const [otherPointId, setOtherPointId] = useState("");
  const [otherAddress, setOtherAddress] = useState("");
  const [otherQuartier, setOtherQuartier] = useState("");
  const [mainAddress, setMainAddress] = useState("");
  const [mainQuartier, setMainQuartier] = useState("");

  const [sName, setSName] = useState("Boutique Mama Ngo");
  const [sPhone, setSPhone] = useState("699 11 22 33");
  const [sKind, setSKind] = useState<UrbanItem["senderKind"]>("Commerçant");
  const [rName, setRName] = useState("Nkoulou Brigitte");
  const [rPhone, setRPhone] = useState("677 45 67 89");

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [creneau, setCreneau] = useState("14:00–16:00");
  const [express, setExpress] = useState(false);

  const [nbColis, setNbColis] = useState("1");
  const [poids, setPoids] = useState("3");
  const [nature, setNature] = useState("Vêtements");
  const [valeur, setValeur] = useState("25000");
  const [cod, setCod] = useState("0");
  const [lead, setLead] = useState("WhatsApp");
  const [insurance, setInsurance] = useState(false);
  const [notify, setNotify] = useState(true);

  // Step 3
  const [payMode, setPayMode] = useState<"direct" | "ref" | "recipient" | "later">("direct");
  const [refOM, setRefOM] = useState("");
  const [refTouch, setRefTouch] = useState("");

  const [generatedCode, setGeneratedCode] = useState("");

  if (!isOpen) return null;

  const isCollectOnly = service === "Collecte";
  const mainPoint = mainSel && mainSel !== "domicile" ? URBAN_POINTS.find((p) => p.id === mainSel) : undefined;
  const mainSide: Side = { type: mainSel === "domicile" ? "Domicile" : mainPoint?.type ?? "Agence", pointId: mainPoint?.id ?? "", address: mainAddress, quartier: mainQuartier };
  const otherSide: Side = { type: otherType, pointId: otherPointId, address: otherAddress, quartier: otherQuartier };
  const pickup = isCollectOnly ? mainSide : otherSide;
  const drop = isCollectOnly ? otherSide : mainSide;

  // Fees
  const numPoids = toFloat(poids, 0);
  const numVal = toFloat(valeur, 0);
  const numCod = toFloat(cod, 0);
  const base = BASE_FEE[city] ?? DEFAULT_BASE_FEE;
  const extraKg = Math.max(0, Math.ceil(numPoids - 5)) * EXTRA_KG_FEE;
  const pickupFee = (pickup.type === "Domicile" ? HOME_PICKUP_FEE : 0) + sideFee(pickup);
  const dropFee = (drop.type === "Domicile" ? HOME_DELIVERY_FEE : 0) + sideFee(drop);
  const expressFee = express ? EXPRESS_FEE : 0;
  const codFee = numCod > 0 ? Math.max(500, numCod * COD_RATE) : 0;
  const assur = insurance ? numVal * INSURANCE_RATE : 0;
  const total = base + extraKg + pickupFee + dropFee + expressFee + codFee + assur;

  const cityPoints = URBAN_POINTS.filter((p) => {
    if (p.city !== city) return false;
    if (filterType !== "Tous" && p.type !== filterType) return false;
    if (searchPoint.trim()) {
      const q = searchPoint.toLowerCase();
      if (![p.name, p.quartier, p.type].join(" ").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const showHome = filterType === "Tous" || filterType === "Domicile";

  const reset = () => {
    setStep(1);
    setMainSel(null);
    setOtherPointId("");
    setGeneratedCode("");
  };

  const handleNext = () => {
    if (step === 1) {
      if (!mainSel) {
        onFlash(isCollectOnly ? "Choisis le lieu de collecte." : "Choisis le point de remise.");
        return;
      }
      // Default the other side to the first agency of the city.
      if (!otherPointId && otherType !== "Domicile") setOtherPointId(pointsOf(city, otherType)[0]?.id ?? "");
      setStep(2);
    } else if (step === 2) {
      if (!sName.trim() || !rName.trim() || !sPhone.trim() || !rPhone.trim()) {
        onFlash("Renseigne l'expéditeur et le destinataire (noms et téléphones).");
        return;
      }
      if (!sidePlace(pickup).trim() || !sidePlace(drop).trim()) {
        onFlash("Précise le lieu de collecte et le lieu de remise.");
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (payMode === "ref" && !refOM.trim() && !refTouch.trim()) {
        onFlash("Saisis la référence Orange Money ou InTouch.");
        return;
      }
      const now = new Date();
      const dm = `${String(now.getDate()).padStart(2, "0")}${String(now.getMonth() + 1).padStart(2, "0")}`;
      const code = `LIV-${dm}-${CITY_CODE[city] ?? "CMR"}-0${Math.floor(100 + Math.random() * 899)}`;
      const paidNow = payMode === "direct" || payMode === "ref";
      const item: UrbanItem = {
        id: `u${Date.now()}`,
        code,
        createdBy: "Vous",
        createdAt: formatFrDateTime(now),
        source: lead,
        service,
        city,
        sender: sName,
        senderPhone: `+237 ${sPhone}`,
        senderKind: sKind,
        receiver: rName,
        receiverPhone: `+237 ${rPhone}`,
        pickupType: pickup.type,
        pickupPlace: sidePlace(pickup),
        pickupQuartier: sideQuartier(pickup),
        dropType: drop.type,
        dropPlace: sidePlace(drop),
        dropQuartier: sideQuartier(drop),
        date,
        creneau,
        colis: parseInt(nbColis, 10) || 1,
        poids: numPoids,
        nature,
        valeur: numVal,
        cod: numCod,
        codCollected: false,
        montant: total,
        paid: paidNow ? total : 0,
        pay: paidNow ? "Payé" : "Non payé",
        refOM: payMode === "ref" && refOM ? refOM : "—",
        refTouch: payMode === "ref" && refTouch ? refTouch : "—",
        canal: payMode === "ref" ? (refOM ? "Orange Money" : "Touch / ITX") : payMode === "direct" ? "Lien Mobile Money" : "—",
        confirmed: false,
        coursier: "",
        vehicule: "",
        statut: "Demande reçue",
        statutInfo: payMode === "recipient" ? "Frais payés par le destinataire à la remise" : paidNow ? "Paiement à valider" : "Devis envoyé",
        codeRetrait: "",
        tentatives: 0,
        audit: `${formatFrDateTime(now)} création (${lead})`,
        note: express ? "Express même jour" : "",
      };
      if (payMode === "recipient") item.cod = numCod + total;
      setGeneratedCode(code);
      setStep(4);
      onSuccess(item);
    } else {
      reset();
      onClose();
    }
  };

  const handleBack = () => {
    if (step === 1) onClose();
    else if (step === 4) reset();
    else setStep(step - 1);
  };

  const inputCls = "w-full h-9 px-3 border border-indigo-200 rounded-lg text-xs bg-white";
  const labelCls = "block text-[11px] font-semibold text-gray-700 mb-0.5";

  const stepBadge = (n: number, label: string, last = false) => (
    <div className={classNames("flex items-center gap-2 whitespace-nowrap", step === n ? "text-[#1f1b2e] font-bold" : step > n ? "text-emerald-700 font-semibold" : "text-gray-400 font-medium")}>
      <span className={classNames("w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white", step === n ? "bg-[#2563eb]" : step > n ? "bg-emerald-600" : "bg-gray-300 text-gray-700")}>
        {step > n ? <CheckIcon className="w-3.5 h-3.5" /> : n}
      </span>
      <span>{label}</span>
      {!last && <span className="text-gray-300 font-normal ml-3">—</span>}
    </div>
  );

  // Selector for the "other" side of the trip (step 2).
  const renderOtherSide = () => {
    const pts = pointsOf(city, otherType);
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Type de lieu</label>
          <select
            value={otherType}
            onChange={(e) => {
              const t = e.target.value as PointType;
              setOtherType(t);
              setOtherPointId(t === "Domicile" ? "" : pointsOf(city, t)[0]?.id ?? "");
            }}
            className={inputCls}
          >
            {POINT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        {otherType === "Domicile" ? (
          <>
            <div>
              <label className={labelCls}>Adresse / repère</label>
              <input value={otherAddress} onChange={(e) => setOtherAddress(e.target.value)} placeholder="ex. Face pharmacie, 2e portail bleu" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Quartier</label>
              <input value={otherQuartier} onChange={(e) => setOtherQuartier(e.target.value)} placeholder="ex. Makepe" className={inputCls} />
            </div>
          </>
        ) : (
          <div className="md:col-span-2">
            <label className={labelCls}>{otherType} à {city}</label>
            {pts.length ? (
              <select value={otherPointId} onChange={(e) => setOtherPointId(e.target.value)} className={inputCls}>
                {pts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.quartier}
                  </option>
                ))}
              </select>
            ) : (
              <div className="h-9 px-3 flex items-center rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                Aucun point de ce type à {city} — choisis un autre type.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-black/55 backdrop-blur-xs animate-fade-in font-['Public_Sans',sans-serif]">
      <div className="bg-[#f8f7fc] rounded-2xl border border-gray-200 shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden text-[#1f1b2e]">
        {/* Header */}
        <div className="p-4 md:p-5 bg-white border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-xs bg-[#2563eb]" />
              <h2 className="text-lg font-bold text-[#1f1b2e]">Nouvelle livraison urbaine</h2>
            </div>
            <div className="flex items-center gap-6 mt-3 text-xs overflow-x-auto">
              {stepBadge(1, isCollectOnly ? "Lieu de collecte" : "Point de remise")}
              {stepBadge(2, "Saisie de la livraison")}
              {stepBadge(3, "Récapitulatif & paiement", true)}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 tracking-scrollbar space-y-4 text-xs">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-bold text-[#241f36]">
                  {isCollectOnly ? "Où collecter le colis ?" : "Où remettre le colis ?"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Domicile, point relais, locker (consigne), supermarché, agence ou commerçant partenaire.
                </p>
              </div>

              {/* Service type */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {(["Livraison", "Collecte", "Collecte + livraison"] as UrbanService[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setService(s);
                      setMainSel(null);
                      // Door-to-door by default for "Collecte + livraison"; agency drop-off otherwise.
                      setOtherType(s === "Collecte + livraison" ? "Domicile" : "Agence");
                      setOtherPointId("");
                    }}
                    className={classNames(
                      "p-3 rounded-xl border-2 text-left transition-all",
                      service === s ? "border-blue-600 bg-blue-50/50" : "border-gray-200 bg-white hover:border-blue-200"
                    )}
                  >
                    <div className="font-bold text-gray-900">{s}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {s === "Livraison"
                        ? "Le colis est déposé en agence ou au point, puis livré."
                        : s === "Collecte"
                        ? "On récupère le colis chez l'expéditeur ou au point."
                        : "Collecte chez l'expéditeur puis remise au destinataire."}
                    </div>
                  </button>
                ))}
              </div>

              <input
                type="text"
                value={searchPoint}
                onChange={(e) => setSearchPoint(e.target.value)}
                placeholder="Rechercher un point, un quartier, un supermarché, un commerçant..."
                className="w-full h-10 px-3.5 bg-[#e9e6f2] border-0 rounded-xl text-xs text-[#1f1b2e] focus:outline-none focus:ring-2 focus:ring-blue-400"
              />

              <div className="flex items-center gap-2.5 flex-wrap">
                <div className="flex p-0.5 bg-[#e0e7ff] rounded-lg gap-1 flex-wrap">
                  {(["Tous", ...POINT_TYPES] as ("Tous" | PointType)[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setFilterType(t)}
                      className={classNames(
                        "px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer transition-all whitespace-nowrap",
                        filterType === t ? "bg-white text-[#1e3a8a] shadow-xs" : "text-[#64748b] hover:text-[#1e3a8a]"
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <select
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setMainSel(null);
                    setOtherPointId("");
                  }}
                  className="h-9 px-2.5 bg-white border border-[#e2e8f0] rounded-lg text-xs text-[#1f1b2e]"
                >
                  {URBAN_CITIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    setSearchPoint("");
                    setFilterType("Tous");
                  }}
                  className="h-9 px-3 bg-[#fee2e2] hover:bg-rose-200 text-[#dc2626] font-semibold rounded-lg text-xs cursor-pointer"
                >
                  Réinitialiser
                </button>
              </div>

              <div className="grid gap-2.5 pt-1">
                {showHome && (
                  <div
                    onClick={() => setMainSel("domicile")}
                    className={classNames(
                      "p-3.5 bg-white rounded-xl border transition-all flex items-center justify-between gap-3 cursor-pointer",
                      mainSel === "domicile" ? "border-blue-600 ring-2 ring-blue-300/40 bg-blue-50/40" : "border-[#e0e7ff] hover:border-blue-200"
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: POINT_STYLE.Domicile.bg, color: POINT_STYLE.Domicile.fg }}>
                        <HomeIcon className="w-5 h-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-bold text-gray-900">{isCollectOnly ? "Collecte à domicile / sur le lieu de l'expéditeur" : "Livraison à domicile / au bureau"}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          Partout à {city} · {isCollectOnly ? `+${money(HOME_PICKUP_FEE)}` : `+${money(HOME_DELIVERY_FEE)}`}
                        </div>
                      </div>
                    </div>
                    <span className={classNames("h-9 px-3.5 shrink-0 rounded-lg text-xs font-bold border flex items-center gap-1", mainSel === "domicile" ? "bg-[#2563eb] text-white border-[#2563eb]" : "bg-white text-[#241f36] border-gray-300")}>
                      {mainSel === "domicile" ? (
                        <>
                          Sélectionné <CheckIcon className="w-3.5 h-3.5" />
                        </>
                      ) : (
                        "Sélectionner"
                      )}
                    </span>
                  </div>
                )}

                {cityPoints.map((p) => {
                  const isSel = mainSel === p.id;
                  const free = p.capacity - p.used;
                  const isFull = free <= 0;
                  const pct = Math.round((p.used / p.capacity) * 100);
                  const barColor = pct > 85 ? "#ef4444" : pct > 60 ? "#f59e0b" : "#22c55e";
                  const S = POINT_STYLE[p.type];
                  return (
                    <div
                      key={p.id}
                      onClick={() => !isFull && setMainSel(p.id)}
                      className={classNames(
                        "p-3.5 bg-white rounded-xl border transition-all flex items-center justify-between gap-3 flex-wrap md:flex-nowrap",
                        isFull ? "opacity-70 cursor-not-allowed" : "cursor-pointer",
                        isSel ? "border-blue-600 ring-2 ring-blue-300/40 bg-blue-50/40" : "border-[#e0e7ff] hover:border-blue-200"
                      )}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="w-9 h-9 shrink-0 rounded-lg font-bold text-[11px] flex items-center justify-center" style={{ backgroundColor: S.bg, color: S.fg }}>
                          {S.abbr}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-bold text-gray-900 truncate">{p.name}</span>
                            <span className="shrink-0 px-2 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: S.bg, color: S.fg }}>
                              {p.type}
                            </span>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                            {p.city} · {p.quartier} · {p.horaires}
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-3">
                        <div className="w-28 whitespace-nowrap">
                          <div className={classNames("text-xs font-bold text-right", isFull ? "text-rose-600" : free / p.capacity < 0.2 ? "text-orange-600" : "text-emerald-700")}>
                            {isFull ? "Complet" : `${free} ${p.type === "Locker" ? "casiers libres" : "places libres"}`}
                          </div>
                          <div className="h-1.5 bg-gray-200 rounded-full mt-1.5 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                          </div>
                          <div className="text-[10px] text-gray-400 text-right mt-1 font-medium">{p.fee ? `+${money(p.fee)}` : "Sans frais"}</div>
                        </div>
                        <span
                          className={classNames(
                            "h-9 px-3.5 rounded-lg text-xs font-bold border flex items-center gap-1",
                            isSel ? "bg-[#2563eb] text-white border-[#2563eb]" : isFull ? "bg-gray-100 text-gray-400 border-gray-200" : "bg-white text-[#241f36] border-gray-300"
                          )}
                        >
                          {isSel ? (
                            <>
                              Sélectionné <CheckIcon className="w-3.5 h-3.5" />
                            </>
                          ) : isFull ? (
                            "Complet"
                          ) : (
                            "Sélectionner"
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {!showHome && cityPoints.length === 0 && (
                  <div className="p-6 text-center text-gray-500 border border-dashed border-gray-300 rounded-xl">
                    Aucun point « {filterType} » à {city} pour le moment.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="p-3.5 bg-[#1e40af] rounded-xl text-white flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase font-bold text-blue-200 tracking-wider">
                    {service} · {city} · {isCollectOnly ? "Lieu de collecte" : "Point de remise"}
                  </div>
                  <div className="text-sm font-bold mt-0.5">{mainSel === "domicile" ? (isCollectOnly ? "Collecte à domicile" : "Livraison à domicile") : mainPoint?.name}</div>
                  <div className="text-xs text-blue-100">{mainSel === "domicile" ? `Adresse à préciser ci-dessous` : `${mainPoint?.quartier} · ${mainPoint?.horaires}`}</div>
                </div>
                <div className="text-right text-xs font-bold">{mainSel === "domicile" ? "" : mainPoint?.type}</div>
              </div>

              {mainSel === "domicile" && (
                <div className="p-4 bg-white border border-[#ece7f8] rounded-xl grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Adresse / repère ({isCollectOnly ? "collecte" : "livraison"})</label>
                    <input value={mainAddress} onChange={(e) => setMainAddress(e.target.value)} placeholder="ex. Carrefour Ndokoti, immeuble jaune" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Quartier</label>
                    <input value={mainQuartier} onChange={(e) => setMainQuartier(e.target.value)} placeholder="ex. Ndokoti" className={inputCls} />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white border border-[#ece7f8] rounded-xl space-y-2.5">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#c2410c]">Expéditeur</div>
                  <div>
                    <label className={labelCls}>Nom / raison sociale</label>
                    <input value={sName} onChange={(e) => setSName(e.target.value)} className={inputCls} />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className={labelCls}>Téléphone / WhatsApp (+237)</label>
                      <input value={sPhone} onChange={(e) => setSPhone(e.target.value)} className={inputCls} />
                    </div>
                    <div className="w-36">
                      <label className={labelCls}>Type</label>
                      <select value={sKind} onChange={(e) => setSKind(e.target.value as UrbanItem["senderKind"])} className={inputCls}>
                        <option value="Particulier">Particulier</option>
                        <option value="Commerçant">Commerçant</option>
                        <option value="Entreprise">Entreprise</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white border border-[#ece7f8] rounded-xl space-y-2.5">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#15803d]">Destinataire</div>
                  <div>
                    <label className={labelCls}>Nom et prénoms</label>
                    <input value={rName} onChange={(e) => setRName(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Téléphone / WhatsApp (+237) — reçoit le code de retrait</label>
                    <input value={rPhone} onChange={(e) => setRPhone(e.target.value)} className={inputCls} />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white border border-[#ece7f8] rounded-xl space-y-3">
                <div className="text-sm font-bold text-[#241f36]">
                  {isCollectOnly ? "Où apporter le colis collecté ?" : service === "Livraison" ? "Où l'expéditeur dépose-t-il le colis ?" : "Où collecter le colis ?"}
                </div>
                {renderOtherSide()}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-gray-100">
                  <div>
                    <label className={labelCls}>Date souhaitée</label>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Créneau</label>
                    <select value={creneau} onChange={(e) => setCreneau(e.target.value)} className={inputCls}>
                      {["08:00–10:00", "10:00–12:00", "12:00–14:00", "14:00–16:00", "16:00–18:00", "18:00–20:00"].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer md:pt-5">
                    <input type="checkbox" checked={express} onChange={(e) => setExpress(e.target.checked)} className="w-4 h-4 accent-blue-700" />
                    <span>Express même jour (+{money(EXPRESS_FEE)})</span>
                  </label>
                </div>
              </div>

              <div className="p-4 bg-white border border-[#ece7f8] rounded-xl space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-[#5b21b6]">Colis</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className={labelCls}>Nombre de colis</label>
                    <input type="number" min={1} value={nbColis} onChange={(e) => setNbColis(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Poids total (kg)</label>
                    <input type="number" step="0.5" min={0} value={poids} onChange={(e) => setPoids(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Nature</label>
                    <select value={nature} onChange={(e) => setNature(e.target.value)} className={inputCls}>
                      {["Vêtements", "Documents", "Alimentaire", "Médicaments", "Cosmétiques", "Électronique", "Pièces détachées", "Autre"].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Valeur déclarée (FCFA)</label>
                    <input type="number" min={0} value={valeur} onChange={(e) => setValeur(e.target.value)} className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Contre-remboursement à encaisser (FCFA)</label>
                    <input type="number" min={0} value={cod} onChange={(e) => setCod(e.target.value)} className={inputCls} />
                    <div className="text-[10px] text-gray-400 mt-0.5">Montant payé par le destinataire à la remise (e-commerce, commerçants). 0 = aucun.</div>
                  </div>
                  <div>
                    <label className={labelCls}>Source de la demande</label>
                    <select value={lead} onChange={(e) => setLead(e.target.value)} className={inputCls}>
                      {["WhatsApp", "Facebook Ads", "TikTok", "Site web", "Commerçant partenaire", "Appel entrant"].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-4 flex-wrap pt-2 border-t border-gray-100">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={insurance} onChange={(e) => setInsurance(e.target.checked)} className="w-4 h-4 accent-blue-700" />
                    <span>Assurance (1 % de la valeur)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="w-4 h-4 accent-blue-700" />
                    <span>SMS / WhatsApp avec code de retrait et suivi</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-5 bg-[#1e3a8a] text-white rounded-xl space-y-3">
                <div className="text-lg font-bold">Confirmation et paiement</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div className="p-2.5 rounded-lg bg-white/10">
                    <div className="text-[10px] uppercase font-bold text-blue-200">Collecte</div>
                    <div className="font-bold">{pickup.type} · {sidePlace(pickup)}</div>
                    <div className="text-blue-100">{sideQuartier(pickup)}</div>
                  </div>
                  <div className="p-2.5 rounded-lg bg-white/10">
                    <div className="text-[10px] uppercase font-bold text-blue-200">Remise</div>
                    <div className="font-bold">{drop.type} · {sidePlace(drop)}</div>
                    <div className="text-blue-100">
                      {sideQuartier(drop)} · {date.split("-").reverse().join("/")} {creneau}
                    </div>
                  </div>
                </div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-blue-200">Détail des frais</div>
                <div className="divide-y divide-blue-800 text-xs">
                  <FeeRow label={`Course urbaine ${city}`} value={base} />
                  {extraKg > 0 && <FeeRow label={`Supplément poids (${Math.ceil(numPoids - 5)} kg au-delà de 5 kg)`} value={extraKg} />}
                  {pickupFee > 0 && <FeeRow label={`Collecte (${pickup.type.toLowerCase()})`} value={pickupFee} />}
                  {dropFee > 0 && <FeeRow label={`Remise (${drop.type.toLowerCase()})`} value={dropFee} />}
                  {expressFee > 0 && <FeeRow label="Express même jour" value={expressFee} />}
                  {codFee > 0 && <FeeRow label={`Gestion contre-remboursement (${money(numCod)})`} value={codFee} />}
                  {assur > 0 && <FeeRow label="Assurance" value={assur} className="text-amber-200" />}
                </div>
                <div className="pt-3 border-t border-blue-700 flex justify-between items-baseline">
                  <div className="text-sm font-bold">Frais de livraison :</div>
                  <div className="text-2xl font-black text-amber-300">{money(total)}</div>
                </div>
              </div>

              <div className="p-4 bg-white border border-[#ece7f8] rounded-xl space-y-2.5">
                <div className="text-sm font-bold text-gray-900">Mode de paiement des frais</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5">
                  {(
                    [
                      ["direct", "Mobile Money direct", "Lien de paiement par SMS / WhatsApp"],
                      ["ref", "Paiement déjà reçu", "Saisie de la transaction OM ou InTouch"],
                      ["recipient", "Payé par le destinataire", "Ajouté au contre-remboursement"],
                      ["later", "Enregistrer en devis", "Paiement avant la collecte"],
                    ] as const
                  ).map(([k, title, sub]) => (
                    <div
                      key={k}
                      onClick={() => setPayMode(k)}
                      className={classNames("p-3 rounded-xl border cursor-pointer transition-all", payMode === k ? "border-blue-600 bg-blue-50/50" : "border-gray-200 bg-white")}
                    >
                      <div className="font-bold text-xs text-gray-900">{title}</div>
                      <div className="text-[11px] text-gray-500 mt-1">{sub}</div>
                    </div>
                  ))}
                </div>
                {payMode === "ref" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                    <input value={refOM} onChange={(e) => setRefOM(e.target.value)} placeholder="Réf. Orange Money (MP...)" className="h-9 px-3 border border-orange-200 rounded-lg text-xs" />
                    <input value={refTouch} onChange={(e) => setRefTouch(e.target.value)} placeholder="Réf. In Touch (ITX-...)" className="h-9 px-3 border border-cyan-200 rounded-lg text-xs" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="text-center py-6 space-y-4 max-w-md mx-auto">
              <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <CheckCircleIcon className="w-9 h-9" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Livraison enregistrée</h3>
                <div className="text-[11px] text-gray-500 uppercase font-bold tracking-wider mt-2">Numéro de livraison</div>
                <div className="font-mono text-2xl font-bold text-blue-700 mt-0.5">{generatedCode}</div>
              </div>
              <div className="flex gap-2 justify-center pt-2 flex-wrap">
                <button type="button" onClick={() => onFlash(`Bon de livraison PDF téléchargé — ${generatedCode}`)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs">
                  <DocumentArrowDownIcon className="w-4 h-4" />
                  Bon de livraison
                </button>
                <button type="button" onClick={() => onFlash(`Lien de suivi envoyé par WhatsApp à ${rName}`)} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs">
                  <ShareIcon className="w-4 h-4" />
                  Partager WhatsApp
                </button>
              </div>
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs text-left mt-4">
                <strong>Livraison ajoutée à la liste</strong>
                <p className="mt-0.5 text-[11px] text-emerald-700">
                  Elle apparaît dans « Demande / non payé » ou « Payé — à planifier ». Planifie la collecte depuis le Kanban.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-white border-t border-gray-100 flex items-center justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <div className="text-[11px] text-gray-500">{isCollectOnly ? "Lieu de collecte" : "Point de remise"}</div>
            <div className="text-base font-bold text-[#1f1b2e] truncate">
              {mainSel === "domicile" ? "À domicile" : mainPoint ? mainPoint.name : "Aucune sélection"}
            </div>
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <button type="button" onClick={handleBack} className="h-10 px-4 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-lg text-xs cursor-pointer">
              {step === 1 ? "Annuler" : step === 4 ? "Nouvelle livraison" : "Précédent"}
            </button>
            <button
              type="button"
              onClick={handleNext}
              className={classNames(
                "h-10 px-5 text-white font-bold rounded-lg text-xs cursor-pointer shadow-xs transition-colors",
                step === 1 && !mainSel ? "bg-blue-300 cursor-not-allowed" : "bg-[#2563eb] hover:bg-blue-700"
              )}
            >
              {step === 1 ? "Continuer" : step === 2 ? "Voir le récapitulatif" : step === 3 ? "Confirmer" : "Terminer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeeRow({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className={classNames("py-2 flex justify-between gap-3", className)}>
      <span>{label}</span>
      <span className="font-bold whitespace-nowrap">{money(value)}</span>
    </div>
  );
}

// -------------------------------------------------------------
// DETAIL MODAL (click on a delivery)
// -------------------------------------------------------------
interface UrbainDetailModalProps {
  item: UrbanItem | null;
  onClose: () => void;
  onConfirmPay: (id: string, code: string) => void;
  onFlash: (msg: string) => void;
}

export function UrbainDetailModal({ item, onClose, onConfirmPay, onFlash }: UrbainDetailModalProps) {
  if (!item) return null;
  const S = STATUS_STYLE[item.statut];
  const P = PAY_STYLE[item.pay];

  const side = (title: string, color: string, type: PointType, place: string, quartier: string) => (
    <div className="p-3.5 bg-gray-50 rounded-xl space-y-1">
      <span className={classNames("font-bold uppercase text-[10px]", color)}>{title}</span>
      <div className="flex items-center gap-1.5">
        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: POINT_STYLE[type].bg, color: POINT_STYLE[type].fg }}>
          {type}
        </span>
      </div>
      <div className="font-bold text-gray-900">{place || "—"}</div>
      <div className="text-gray-500">{quartier}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in font-['Public_Sans',sans-serif]" onMouseDown={onClose}>
      <div onMouseDown={(e) => e.stopPropagation()} className="bg-white rounded-2xl border border-gray-200 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4 text-[#1f1b2e] text-xs">
        <div className="flex items-start justify-between border-b border-gray-100 pb-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-mono font-bold rounded-md">{item.code}</span>
              <span className="px-2.5 py-1 bg-blue-100 text-blue-800 font-bold rounded-md">{item.service}</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-bold" style={{ backgroundColor: S.bg, color: S.fg }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: S.dot }} />
                {item.statut}
              </span>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mt-2">
              {item.city} · {item.pickupType} → {item.dropType}
            </h2>
            <p className="text-gray-500">
              {item.date.split("-").reverse().join("/")} · {item.creneau}
              {item.coursier ? ` · ${item.coursier} (${item.vehicule})` : " · coursier non affecté"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-3.5 bg-gray-50 rounded-xl space-y-1">
            <span className="font-bold text-orange-800 uppercase text-[10px]">Expéditeur · {item.senderKind}</span>
            <div className="font-bold text-sm text-gray-900">{item.sender}</div>
            <div className="text-gray-600 font-mono">{item.senderPhone}</div>
          </div>
          <div className="p-3.5 bg-gray-50 rounded-xl space-y-1">
            <span className="font-bold text-emerald-800 uppercase text-[10px]">Destinataire</span>
            <div className="font-bold text-sm text-gray-900">{item.receiver}</div>
            <div className="text-gray-600 font-mono">{item.receiverPhone}</div>
          </div>
          {side("Collecte", "text-purple-800", item.pickupType, item.pickupPlace, item.pickupQuartier)}
          {side("Remise", "text-teal-800", item.dropType, item.dropPlace, item.dropQuartier)}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3.5 bg-blue-50/40 border border-blue-100 rounded-xl">
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-400">Colis</span>
            <div className="font-bold text-gray-900">
              {kg(item.poids)} ({item.colis} colis)
            </div>
            <div className="text-[11px] text-gray-500">{item.nature}</div>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-400">Frais</span>
            <div className="font-bold text-sm">{money(item.montant)}</div>
            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: P.bg, color: P.fg }}>
              {item.pay}
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-400">Contre-remboursement</span>
            <div className="font-bold text-gray-900">{item.cod > 0 ? money(item.cod) : "Aucun"}</div>
            {item.cod > 0 && <div className={classNames("text-[11px] font-semibold", item.codCollected ? "text-emerald-700" : "text-rose-700")}>{item.codCollected ? "Encaissé" : "À encaisser"}</div>}
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-400">Code de retrait</span>
            <div className="font-mono font-bold text-gray-900">{item.codeRetrait || "—"}</div>
            {item.tentatives > 0 && <div className="text-[11px] text-orange-700">{item.tentatives} tentative(s) échouée(s)</div>}
          </div>
        </div>

        <div className="p-3 rounded-xl border border-gray-100">
          <div className="text-[10px] uppercase font-bold text-gray-400">Dernière mise à jour</div>
          <div className="text-gray-800 mt-0.5">{item.statutInfo || "—"}</div>
          <div className="text-[10px] uppercase font-bold text-gray-400 mt-2">Historique</div>
          <ul className="mt-1 space-y-0.5 text-[11px] text-gray-600">
            {item.audit.split(" · ").map((a, i) => (
              <li key={i}>• {a}</li>
            ))}
          </ul>
        </div>

        <div className="p-3 bg-gray-50 rounded-xl space-y-2">
          <div className="font-bold text-gray-900">Actions sur cette livraison</div>
          <div className="flex gap-2 flex-wrap">
            {!item.confirmed && item.pay !== "Remboursé" && (
              <button
                type="button"
                onClick={() => {
                  onConfirmPay(item.id, item.code);
                  onClose();
                }}
                className="h-8 px-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg cursor-pointer shadow-xs"
              >
                Confirmer le paiement
              </button>
            )}
            <button type="button" onClick={() => onFlash(`Étiquette imprimée — ${item.code}`)} className="h-8 px-3 border border-blue-200 bg-white hover:bg-blue-50 text-blue-900 font-semibold rounded-lg cursor-pointer">
              Imprimer étiquette
            </button>
            {item.codeRetrait && (
              <button type="button" onClick={() => onFlash(`Code de retrait renvoyé à ${item.receiver}`)} className="h-8 px-3 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg cursor-pointer">
                Renvoyer le code de retrait
              </button>
            )}
            <button type="button" onClick={() => onFlash(`WhatsApp envoyé à ${item.receiver}`)} className="h-8 px-3 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg cursor-pointer">
              WhatsApp destinataire
            </button>
            {item.coursier && (
              <button type="button" onClick={() => onFlash(`Appel vers ${item.coursier}`)} className="h-8 px-3 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg cursor-pointer">
                Appeler le coursier
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
